import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Pencil, Plus, AlertCircle, Wallet, Lock } from 'lucide-react';
import { useEmployee, useSalaryStructures } from '@/composables/payroll/useEmployees';
import { useSalaryStructureBreakdown } from '@/composables/payroll/usePayComponents';
import { useOrganizationSettings } from '@/composables/shared/useOrganizationSettings';
import { PageContainer } from '@/components/layout/PageContainer';
import { SalaryStructureEditor } from '@/components/payroll/SalaryStructureEditor';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { EmptyState } from '@/components/EmptyState';
import { formatCurrency, formatDateUTC } from '@/lib/formatters';
import { EMPLOYMENT_TYPE_OPTIONS, SALARY_REVISION_TYPE_OPTIONS, type SalaryStructure } from '@/types/payroll';

const STATUS_STYLES: Record<string, string> = {
  ACTIVE: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
  ON_LEAVE: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
  RESIGNED: 'bg-slate-500/15 text-slate-600 dark:text-slate-400',
  TERMINATED: 'bg-red-500/15 text-red-600 dark:text-red-400',
};

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-sm">{value || '—'}</p>
    </div>
  );
}

export function EmployeeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { employee, isLoading } = useEmployee(id ?? '');
  const { structures, createStructure } = useSalaryStructures(id ?? '');
  const { settings } = useOrganizationSettings();

  const [adding, setAdding] = useState(false);
  const [selectedStructureId, setSelectedStructureId] = useState<string | undefined>();

  // Default to the newest structure until the user picks another.
  const activeId = selectedStructureId ?? structures[0]?.id;
  // The revision in force today, which is what a raise is measured against.
  const currentStructure = structures.find((st) => st.isCurrent) ?? structures[0];
  const { breakdown } = useSalaryStructureBreakdown(activeId);
  const activeStructure = structures.find((s) => s.id === activeId);

  if (isLoading) return <LoadingSpinner />;
  if (!employee) return <EmptyState title="Employee not found" description="It may have been deleted." />;

  const fmt = (n: number) => formatCurrency(n, activeStructure?.currency ?? 'INR');

  return (
    <PageContainer backTo
      title={`${employee.firstName} ${employee.lastName}`}
      description={[employee.employeeCode, employee.designation].filter(Boolean).join(' · ')}
      actions={
        <>
          <Button variant="outline" onClick={() => navigate('/people/employees')}>
            <ArrowLeft className="mr-2 h-4 w-4" />Back
          </Button>
          <Button onClick={() => navigate(`/people/employees/${employee.id}/edit`)}>
            <Pencil className="mr-2 h-4 w-4" />Edit
          </Button>
        </>
      }
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Badge variant="outline" className={`border-0 font-medium ${STATUS_STYLES[employee.status] ?? ''}`}>
          {employee.status.replace(/_/g, ' ')}
        </Badge>
        <Badge variant="outline">
          {EMPLOYMENT_TYPE_OPTIONS.find((o) => o.value === employee.employmentType)?.label ?? employee.employmentType}
        </Badge>
        {employee.user
          ? <Badge variant="outline" className="border-0 bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">Self-service enabled</Badge>
          : <Badge variant="outline" className="text-muted-foreground">No login</Badge>}
      </div>

      <Tabs defaultValue="profile">
        <TabsList>
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="salary">Salary ({structures.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-4">
          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader><CardTitle className="text-base">Personal</CardTitle></CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2">
                <Field label="Employee code" value={employee.employeeCode} />
                <Field label="Email" value={employee.email} />
                <Field label="Phone" value={employee.phone} />
                <Field label="Date of birth" value={employee.dateOfBirth ? formatDateUTC(employee.dateOfBirth) : null} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Employment</CardTitle></CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2">
                <Field label="Designation" value={employee.designation} />
                <Field label="Department" value={employee.department} />
                <Field label="Date of joining" value={formatDateUTC(employee.dateOfJoining)} />
                <Field label="Date of exit" value={employee.dateOfExit ? formatDateUTC(employee.dateOfExit) : null} />
                <Field label="Linked login" value={employee.user?.email} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Statutory identifiers</CardTitle></CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2">
                <Field label={settings?.taxIdentifierLabel || 'PAN'} value={employee.taxIdentifier} />
                <Field label={settings?.socialSecurityIdLabel || 'ESI Number'} value={employee.socialSecurityId} />
                <Field label={settings?.pensionIdLabel || 'UAN'} value={employee.pensionId} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Salary payout</CardTitle></CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2">
                <Field label="Bank" value={employee.bankName} />
                <Field label="Account holder" value={employee.bankAccountName} />
                <Field label="Account number" value={employee.bankAccountNumber} />
                <Field label="IFSC / routing" value={employee.bankIfsc} />
                {employee.notes && <div className="sm:col-span-2"><Field label="Notes" value={employee.notes} /></div>}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="salary" className="mt-4">
          {adding ? (
            <SalaryStructureEditor
              employeeId={employee.id}
              isFirstRevision={structures.length === 0}
              currentCtc={currentStructure ? Number(currentStructure.ctcAnnual) : null}
              onCancel={() => setAdding(false)}
              isSaving={createStructure.isPending}
              onSave={(data) =>
                createStructure.mutate(data, {
                  onSuccess: (res) => { setAdding(false); setSelectedStructureId(res.data.id); },
                })
              }
            />
          ) : structures.length === 0 ? (
            <EmptyState
              icon={Wallet}
              title="No salary structure yet"
              description="Define how this employee's CTC splits into earnings, deductions and employer contributions."
              actionLabel="Add salary structure"
              onAction={() => setAdding(true)}
            />
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Showing</span>
                  <Select value={activeId} onValueChange={setSelectedStructureId}>
                    <SelectTrigger className="w-[280px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {structures.map((st) => (
                        <SelectItem key={st.id} value={st.id}>
                          {formatDateUTC(st.effectiveFrom)} — {formatCurrency(st.ctcAnnual, st.currency)}/yr
                          {st.isCurrent ? ' (current)' : st.isScheduled ? ' (scheduled)' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={() => setAdding(true)}><Plus className="mr-2 h-4 w-4" />New revision</Button>
              </div>

              {breakdown && (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <StatCard label="Monthly CTC" value={fmt(breakdown.monthlyCtc)} />
                  <StatCard label="Gross earnings" value={fmt(breakdown.grossEarnings)} />
                  <StatCard label="Deductions" value={`− ${fmt(breakdown.totalDeductions)}`} />
                  <StatCard label="Net pay" value={fmt(breakdown.netPay)} highlight />
                </div>
              )}

              {breakdown && (
                <div className="grid gap-4 lg:grid-cols-3">
                  <BreakdownCard title="Earnings" lines={breakdown.earnings} fmt={fmt} total={breakdown.grossEarnings} totalLabel="Gross" />
                  <BreakdownCard title="Deductions" lines={breakdown.deductions} fmt={fmt} total={breakdown.totalDeductions} totalLabel="Total" negative />
                  <BreakdownCard title="Employer contributions" lines={breakdown.employerContribs} fmt={fmt} total={breakdown.employerContributions} totalLabel="Total" />
                </div>
              )}

              {breakdown && Math.abs(breakdown.ctcVariance) > 0.02 && (
                <div className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    {fmt(Math.abs(breakdown.ctcVariance))}/month of the stated CTC is{' '}
                    {breakdown.ctcVariance > 0 ? 'unallocated' : 'over-allocated'}.
                  </p>
                </div>
              )}

              <SalaryHistory
                structures={structures}
                activeId={activeId}
                onSelect={setSelectedStructureId}
              />
            </div>
          )}
        </TabsContent>
      </Tabs>
    </PageContainer>
  );
}

function StatCard({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <Card className={highlight ? 'border-primary/40 bg-primary/5' : undefined}>
      <CardContent className="pt-6">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="mt-1 text-xl font-semibold tabular-nums">{value}</p>
      </CardContent>
    </Card>
  );
}

function BreakdownCard({
  title, lines, fmt, total, totalLabel, negative,
}: {
  title: string;
  lines: { code: string; label: string; amount: number; isStatutory: boolean }[];
  fmt: (n: number) => string;
  total: number;
  totalLabel: string;
  negative?: boolean;
}) {
  if (lines.length === 0) return null;
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        {lines.map((l) => (
          <div key={l.code} className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              {l.label}
              {l.isStatutory && <span className="ml-1 text-[10px] uppercase tracking-wide">stat</span>}
            </span>
            <span className="tabular-nums">{negative ? '− ' : ''}{fmt(l.amount)}</span>
          </div>
        ))}
        <div className="flex items-center justify-between border-t pt-2 text-sm font-semibold">
          <span>{totalLabel}</span>
          <span className="tabular-nums">{negative ? '− ' : ''}{fmt(total)}</span>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * The employee's salary history, newest first.
 *
 * Every pay change is a new revision rather than an edit, so this list is the
 * complete record — each row shows the delta against the revision before it,
 * why it happened and who made it.
 */
function SalaryHistory({
  structures, activeId, onSelect,
}: {
  structures: SalaryStructure[];
  activeId?: string;
  onSelect: (id: string) => void;
}) {
  if (structures.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Salary history</CardTitle>
        <p className="text-sm text-muted-foreground">
          {structures.length} revision{structures.length === 1 ? '' : 's'} on record. Pay changes are
          never edited in place, so past payslips stay reproducible.
        </p>
      </CardHeader>
      <CardContent className="space-y-2">
        {structures.map((st) => {
          const up = (st.changeAmount ?? 0) > 0;
          const down = (st.changeAmount ?? 0) < 0;
          return (
            <button
              key={st.id}
              type="button"
              onClick={() => onSelect(st.id)}
              className={`flex w-full flex-wrap items-center gap-x-3 gap-y-1 rounded-md border p-3 text-left transition-colors hover:bg-accent ${
                st.id === activeId ? 'border-primary/50 bg-primary/5' : ''
              }`}
            >
              <span className="w-24 shrink-0 text-sm text-muted-foreground">
                {formatDateUTC(st.effectiveFrom)}
              </span>

              <span className="font-medium tabular-nums">
                {formatCurrency(st.ctcAnnual, st.currency)}
              </span>

              {st.previousCtcAnnual != null && st.changePercent != null && (
                <span className={`text-xs font-medium ${up ? 'text-emerald-600 dark:text-emerald-400' : down ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'}`}>
                  {up ? '↑' : down ? '↓' : '—'} {up ? '+' : ''}{st.changePercent}%
                  <span className="ml-1 text-muted-foreground">
                    (from {formatCurrency(st.previousCtcAnnual, st.currency)})
                  </span>
                </span>
              )}

              <span className="flex flex-wrap items-center gap-1">
                <Badge variant="outline" className="text-[10px]">
                  {SALARY_REVISION_TYPE_OPTIONS.find((o) => o.value === st.revisionType)?.label ?? st.revisionType}
                </Badge>
                {st.isCurrent && (
                  <Badge variant="outline" className="border-0 bg-emerald-500/15 text-[10px] text-emerald-600 dark:text-emerald-400">
                    current
                  </Badge>
                )}
                {st.isScheduled && (
                  <Badge variant="outline" className="border-0 bg-sky-500/15 text-[10px] text-sky-600 dark:text-sky-400">
                    scheduled
                  </Badge>
                )}
                {st.isLocked && (
                  <Badge variant="outline" className="text-[10px]">
                    <Lock className="mr-1 h-2.5 w-2.5" />{st.payslipCount} payslip{st.payslipCount === 1 ? '' : 's'}
                  </Badge>
                )}
              </span>

              <span className="w-full text-xs text-muted-foreground">
                {st.reason || <span className="italic">no reason recorded</span>}
                {st.createdBy && ` · by ${st.createdBy.firstName} ${st.createdBy.lastName}`}
              </span>
            </button>
          );
        })}
      </CardContent>
    </Card>
  );
}
